// live.tsx — the bridge between the design's screens and the real /v2 API.
// Small shared helpers: the request context from app state, an async-fetch hook, and tiny
// loading / error / "preview" affordances.
import { useState, useEffect, useCallback, type ReactNode } from 'react';

import { ApiError, type AuthContext } from '@/api/client';
import { ME } from '@/api/payouts';

import { Container, Group, Loader, SegmentedControl, SegmentedControlItem, Text } from '@melio/penny';

import { useApp, Banner, Pill } from '@/components/ds';
import { DASHBOARD_ENV_IDS, ENVIRONMENTS, type EnvironmentId } from '@/config/environments';
import type { AppContextValue } from '@/lib/types';

/**
 * The {@link AuthContext} the signed-in dashboard passes to the token-authenticated payouts calls.
 * The dashboard authenticates with the user's access token (Bearer) — no partner api-key.
 */
export function useApiCtx(): AuthContext {
  const app = useApp();
  return { env: app.env, getToken: app.getAccessToken };
}

/**
 * Environment switcher for the signed-in dashboard, scoped to the envs it manages (staging01,
 * production). Lives once in the dashboard shell and drives the shared app env, so every tab —
 * Entities, Webhooks and API keys — reads from the same selection.
 *
 * The dashboard only ever manages two kinds of env — a test sandbox (staging01) and live
 * production — so we render a two-state segmented toggle rather than a dropdown. Production is
 * the money-moving side, so it reads with a critical (red) accent when active; the safe test
 * side reads neutral. Falls back to a static label when only one env is reachable (local dev).
 *
 * The control is disabled for now — production switching isn't available from the dashboard yet
 * (partners aren't cleared to operate live), so the toggle shows the current env and signals that
 * production is coming, without letting the user flip to it. Flip {@link ENV_TOGGLE_DISABLED} once
 * live switching is enabled.
 */
const ENV_TOGGLE_DISABLED = true;

export function EnvSwitcher() {
  const app = useApp();
  const prodId = DASHBOARD_ENV_IDS.find((id) => ENVIRONMENTS[id].live);
  const testId = DASHBOARD_ENV_IDS.find((id) => !ENVIRONMENTS[id].live);
  const isProd = !!ENVIRONMENTS[app.env].live;
  const disabled = ENV_TOGGLE_DISABLED;

  // When live switching is enabled we need both a test and a production env to toggle between; if
  // only one is reachable (local dev narrows the dashboard to staging01) there's nothing to switch,
  // so show a static label. While the toggle is disabled we always render it — Production is
  // display-only, so local dev still sees the real design without any way to flip envs.
  if (!disabled && (!prodId || !testId)) {
    const soleId = DASHBOARD_ENV_IDS[0] ?? app.env;
    return <Pill kind={ENVIRONMENTS[soleId]?.live ? 'critical' : 'neutral'}>{ENVIRONMENTS[soleId]?.label ?? soleId}</Pill>;
  }
  return (
    <SegmentedControl
      name="environment"
      size="small"
      isDisabled={disabled}
      aria-label="Environment"
      title={disabled ? 'Production access is coming soon' : undefined}
      onChange={(e) => {
        const next = (e.target as HTMLInputElement).value === 'production' ? prodId : testId;
        if (next) app.setEnv(next);
      }}
    >
      <SegmentedControlItem value="test" checked={!isProd}>
        <EnvSegmentContent label="Test" active={!isProd} tone="neutral" />
      </SegmentedControlItem>
      <SegmentedControlItem value="production" checked={isProd}>
        <EnvSegmentContent label="Production" active={isProd} tone="critical" />
      </SegmentedControlItem>
    </SegmentedControl>
  );
}

interface EnvSegmentContentProps {
  label: string;
  active: boolean;
  tone: 'neutral' | 'critical';
}
// The segment's chrome (selected/disabled/hover/focus states, sizing) is penny's; all this adds is
// the status dot that tells test and production apart at a glance.
function EnvSegmentContent({ label, active, tone }: EnvSegmentContentProps) {
  const dotColor = tone === 'critical' ? 'var(--critical-700)' : 'var(--success-700)';
  return (
    <Group spacing="xxs" alignItems="center" variant="horizontal">
      <span style={{ width: 7, height: 7, borderRadius: 999, background: active ? dotColor : 'var(--neutral-500)' }} />
      <Text textStyle="body4Semi" color={active && tone === 'critical' ? 'semantic.text.critical.rest' : 'inherit'}>
        {label}
      </Text>
    </Group>
  );
}

/**
 * The signed-in partner's company name for the dashboard top bar, read from the `/me` bootstrap call
 * (the name the partner registered with, resolved into app state on boot). Renders nothing until it's
 * known, so the bar never flashes a placeholder.
 */
export function OrgName() {
  const app = useApp();
  const name = app.companyName;
  if (!name) return null;
  return <Text textStyle="body3Semi">{name}</Text>;
}

/** Self-serve partners address their sole business with the `me` sentinel; trusted partners
 *  use the real org id. */
export function businessIdFor(app: AppContextValue, explicitId?: string | null): string | null {
  if (explicitId) return explicitId;
  return app.partner === 'trusted' ? null : ME;
}

interface AsyncState<T> {
  loading: boolean;
  data: T | null;
  error: unknown | null;
  reload: () => void;
}

interface UseAsyncOptions {
  enabled?: boolean;
}

/** Run an async fn on deps change with cancellation. `enabled:false` skips it. */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  { enabled = true }: UseAsyncOptions = {}
): AsyncState<T> {
  const [state, setState] = useState<{ loading: boolean; data: T | null; error: unknown | null }>({
    loading: enabled,
    data: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    const ctrl = new AbortController();
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(fn(ctrl.signal))
      .then((data) => {
        if (live) setState({ loading: false, data, error: null });
      })
      .catch((error: unknown) => {
        if (live && (error as { name?: string })?.name !== 'AbortError') setState({ loading: false, data: null, error });
      });
    return () => {
      live = false;
      ctrl.abort();
    };
  }, [...deps, nonce, enabled]);
  return { ...state, reload };
}

interface LoadingProps {
  label?: string;
  pad?: number;
}
// penny-backed: <Loader>, which carries its own live region so the label is announced rather than
// just drawn. The previous version paired a decorative spinner with a plain <span>.
export function Loading({ label = 'Loading…', pad = 48 }: LoadingProps) {
  return (
    <Container paddingX="m" paddingY="m" justifyContent="center" alignItems="center" style={{ padding: pad }}>
      <Loader loadingText={label} />
    </Container>
  );
}

interface ErrorBannerProps {
  error: unknown;
  onRetry?: () => void;
}
export function ErrorBanner({ error, onRetry }: ErrorBannerProps) {
  const msg = error instanceof ApiError ? `${error.status} · ${error.message}` : (error as Error)?.message || String(error);
  return (
    <Banner kind="critical" cta={onRetry ? 'Retry' : undefined} onCta={onRetry}>
      <strong>Request failed.</strong> {msg}
    </Banner>
  );
}

interface PreviewBadgeProps {
  children?: ReactNode;
}
/** A small "Preview" tag for screens the design includes but the API can't back yet. */
export function PreviewBadge({ children = 'Preview' }: PreviewBadgeProps) {
  return (
    <Pill kind="warning" style={{ marginLeft: 8 }}>
      {children}
    </Pill>
  );
}

interface PreviewNoteProps {
  children?: ReactNode;
}
/** Banner explaining a screen is design-only until the backend ships the endpoint. */
export function PreviewNote({ children }: PreviewNoteProps) {
  return (
    <Banner kind="warning" style={{ marginBottom: 20 }}>
      <strong>Preview.</strong> {children} The data below is illustrative — see the API support TODO.
    </Banner>
  );
}
